import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getProxy,
  getSite,
  registerProxy,
  updateProxy,
  getUnassignedProxies,
  assignProxy,
} from "./proxyService.js";
import { resolveNetworkSettings } from "./networkSettings.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Collapse,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Alert,
  Badge,
  Spinner,
  InputGroup,
  InputGroupText,
} from "reactstrap";
// core components
import HusariaHeader from "views/pages/components/HusariaHeader.js";
import { serverHost } from "variables/serverdata.js";

function ProxyForm() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const location = useLocation();

  const labelStyle = {
    color: isDark ? "#ffffff" : "#000000",
  };

  const sectionHeadingStyle = {
    color: isDark ? "#f5f0f0" : "#181212",
  };

  const secondaryTextStyle = {
    color: isDark ? "#cbd5e1" : "#4b5563",
  };

  const metadataLabelStyle = {
    color: isDark ? "#f3f4f6" : "#374151",
    fontWeight: 600,
  };

  const gpfOptions = ["GPF1", "GPF2", "GPF3", "GPF4", "GPF5"];
  const optionalSaltoFieldOptions = [
    { value: "HUSARIAONE", label: "HusariaOne" },
    ...gpfOptions.map((value) => ({ value, label: value })),
  ];

  const [saltoShipOpen, setSaltoShipOpen] = useState(false);
  const [advancedProxyOpen, setAdvancedProxyOpen] = useState(false);
  const [showSaltoShipPassword, setShowSaltoShipPassword] = useState(false);

  const normalizeProxyResponse = (data, site_uuid) => ({
    proxy_name: data.proxy_name || "",
    proxy_ip: data.proxy_ip || "",
    network_mode: data.network_mode || "dhcp",
    static_ip: data.static_ip || "",
    static_prefix: Number(data.static_prefix ?? 24),
    static_gateway: data.static_gateway || "",
    static_dns: data.static_dns || "",
    proxy_uuid: data.proxy_uuid || "",
    proxy_version: data.proxy_version || "",
    mqtt_topic: data.mqtt_topic || "",
    enable: data.enable || "y",
    site_id: data.site_uuid || site_uuid,
    last_checkin: data.last_checkin || null,
    created_at: data.created_at || null,
    latest_version: data.latest_version || "",
    update_available: data.update_available || false,

    proxy_checkin_interval_seconds: Number(data.proxy_checkin_interval_seconds ?? 60),
    device_discovery_subnets: data.device_discovery_subnets || "",
    device_discovery_probe_ports: Array.isArray(data.device_discovery_probe_ports)
      ? data.device_discovery_probe_ports
      : [80, 443, 5060],
    device_discovery_max_hosts: Number(data.device_discovery_max_hosts ?? 512),

    salto_sync_enabled: Boolean(data.salto_sync_enabled),
    salto_ship_url: data.salto_ship_url || "",
    salto_ship_username: data.salto_ship_username || "",
    // The backend intentionally never returns the stored password.
    salto_ship_password: "",
    salto_ship_password_set: Boolean(data.salto_ship_password_set),
    salto_vnumber_gpf: data.salto_vnumber_gpf || "GPF1",
    salto_phone1_gpf: data.salto_phone1_gpf || "GPF2",
    salto_phone2_gpf: data.salto_phone2_gpf || "HUSARIAONE",
    salto_phone3_gpf: data.salto_phone3_gpf || "HUSARIAONE",
    salto_pin_gpf: data.salto_pin_gpf || "HUSARIAONE",
    mqtt_broker: data.mqtt_broker || "mqtt02.husariatech.com",
    mqtt_port: Number(data.mqtt_port ?? 8883),
    // Leave the username empty when the backend did not return one. The
    // backend applies the configured production default instead of the UI
    // silently forcing the legacy plaintext "admin" account.
    mqtt_username: data.mqtt_username || "",
    mqtt_password: "",
    mqtt_password_set: Boolean(data.mqtt_password_set),
    mqtt_tls_enabled: data.mqtt_tls_enabled == null ? true : Boolean(data.mqtt_tls_enabled),

    device_api_username: data.device_api_username || "admin",
    device_api_password: "",
    device_api_password_set: Boolean(data.device_api_password_set),
  });

  // Remove proxyId usage, always use site_id from current user
  const [proxyData, setProxyData] = useState({
    proxy_name: '',
    proxy_ip: '',
    network_mode: "dhcp",
    static_ip: "",
    static_prefix: 24,
    static_gateway: "",
    static_dns: "",
    proxy_uuid: '',
    proxy_version: '',
    mqtt_topic: '',
    enable: '',
    site_id: '',
    last_checkin: null,
    created_at: null,
    latest_version: "",
    update_available: false,

    proxy_checkin_interval_seconds: 60,
    device_discovery_subnets: "",
    device_discovery_probe_ports: [80, 443, 5060],
    device_discovery_max_hosts: 512,

    salto_sync_enabled: false,
    salto_ship_url: "",
    salto_ship_username: "",
    salto_ship_password: "",
    salto_ship_password_set: false,
    salto_vnumber_gpf: "GPF1",
    salto_phone1_gpf: "GPF2",
    salto_phone2_gpf: "HUSARIAONE",
    salto_phone3_gpf: "HUSARIAONE",
    salto_pin_gpf: "HUSARIAONE",

    mqtt_broker: "mqtt02.husariatech.com",
    mqtt_port: 8883,
    mqtt_username: "",
    mqtt_password: "",
    mqtt_password_set: false,
    mqtt_tls_enabled: true,

    device_api_username: "admin",
    device_api_password: "",
    device_api_password_set: false,
  });

  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState({ show: false, success: false, message: '' });
  const [savedProxy, setSavedProxy] = useState(null);
  const [updateSending, setUpdateSending] = useState(false);
  const [proxyUpdating, setProxyUpdating] = useState(false);
  const [supportPasswordLoading, setSupportPasswordLoading] = useState(false);
  const [supportAccess, setSupportAccess] = useState(null);
  const [unassignedProxies, setUnassignedProxies] = useState([]);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [assigningProxyUuid, setAssigningProxyUuid] = useState(null);
  const [assignmentDrafts, setAssignmentDrafts] = useState({});

  const statusProxy = savedProxy;
  const lastCheckin = statusProxy?.last_checkin
    ? new Date(statusProxy.last_checkin)
    : null;

  const isOnline =
    lastCheckin &&
    Date.now() - lastCheckin.getTime() <= 5 * 60 * 1000;

  const loadUnassignedProxies = async () => {
    setUnassignedLoading(true);

    try {
      const proxies = await getUnassignedProxies();
      setUnassignedProxies(proxies);

      setAssignmentDrafts((current) => {
        const next = { ...current };

        proxies.forEach((proxy) => {
          if (!next[proxy.proxy_uuid]) {
            next[proxy.proxy_uuid] = {
              proxy_name: proxy.proxy_name || "",
              enrollment_code: "",
              mqtt_topic: proxy.mqtt_topic || "",
            };
          }
        });

        return next;
      });
    } catch (err) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Error loading unassigned proxies: " + err.message,
      });
    } finally {
      setUnassignedLoading(false);
    }
  };

  const handleAssignmentDraftChange = (proxyUuid, field, value) => {
    setAssignmentDrafts((current) => ({
      ...current,
      [proxyUuid]: {
        ...(current[proxyUuid] || {}),
        [field]: value,
      },
    }));
  };

  const handleAssignProxy = async (proxy) => {
    const { site_uuid } = getSite();
    const draft = assignmentDrafts[proxy.proxy_uuid] || {};

    const enrollmentCodePrefix = (draft.enrollment_code || "").trim();
    const enrollmentCodeHint = (proxy.enrollment_code_hint || "").trim();
    const enrollmentCode = enrollmentCodePrefix + "-" + enrollmentCodeHint;
    const mqttTopic = (draft.mqtt_topic || "").trim();

    if (!site_uuid) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Select a site before assigning a proxy.",
      });
      return;
    }

    if (!mqttTopic) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Enter an MQTT topic before assigning the proxy.",
      });
      return;
    }

    if (enrollmentCodePrefix.length !== 4) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Enter the first four enrollment-code characters shown on the proxy.",
      });
      return;
    }

    if (enrollmentCodeHint.length !== 4) {
      setSaveStatus({
        show: true,
        success: false,
        message: "The proxy enrollment-code hint is unavailable. Refresh the unassigned proxies and try again.",
      });
      return;
    }

    setAssigningProxyUuid(proxy.proxy_uuid);

    try {
      await assignProxy({
        proxy_uuid: proxy.proxy_uuid,
        site_uuid,
        enrollment_code: enrollmentCode,
        mqtt_topic: mqttTopic,
      });

      setSaveStatus({
        show: true,
        success: true,
        message: "Proxy assigned successfully.",
      });

      await loadUnassignedProxies();
      await loadProxyData(site_uuid);
    } catch (err) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Error assigning proxy: " + err.message,
      });
    } finally {
      setAssigningProxyUuid(null);
    }
  };

  // Listen for site changes
  useEffect(() => {
    const handleSiteChange = (event) => {
      console.log("Site changed to:", event.detail);
      loadProxyData(event.detail);
      loadUnassignedProxies();
    };

    window.addEventListener('siteChanged', handleSiteChange);
    return () => {
      window.removeEventListener('siteChanged', handleSiteChange);
    };
  }, []);

  // Load proxy data based on site_uuid
  const loadProxyData = async (site_uuid) => {
    setLoading(true);
    console.log("Loading proxy data for site:", site_uuid);

    try {
      const data = await getProxy(site_uuid);

      if (data) {
        const normalized = normalizeProxyResponse(data, site_uuid);

        setProxyData(normalized);
        setSavedProxy(normalized);

      } else {
        const defaults = {
          proxy_name: "",
          proxy_ip: "",
          network_mode: "dhcp",
          static_ip: "",
          static_prefix: 24,
          static_gateway: "",
          static_dns: "",
          proxy_version: "",
          mqtt_topic: "",
          enable: "",
          site_id: site_uuid,
          last_checkin: null,
          created_at: null,
          proxy_uuid: null,
          latest_version: "",
          update_available: false,
          proxy_checkin_interval_seconds: 60,
          device_discovery_subnets: "",
          device_discovery_probe_ports: [80, 443, 5060],
          device_discovery_max_hosts: 512,
          salto_sync_enabled: false,
          salto_ship_url: "",
          salto_ship_username: "",
          salto_ship_password: "",
          salto_ship_password_set: false,
          salto_vnumber_gpf: "GPF1",
          salto_phone1_gpf: "GPF2",
          salto_phone2_gpf: "HUSARIAONE",
          salto_phone3_gpf: "HUSARIAONE",
          salto_pin_gpf: "HUSARIAONE",
          mqtt_broker: "mqtt02.husariatech.com",
          mqtt_port: 8883,
          mqtt_username: "",
          mqtt_password: "",
          mqtt_password_set: false,
          mqtt_tls_enabled: true,

          device_api_username: "admin",
          device_api_password: "",
          device_api_password_set: false,
        };

        setProxyData(defaults);
        setSavedProxy(null);
      }

    } catch (err) {
      console.error("Error loading proxy:", err);
      setSaveStatus({
        show: true,
        success: false,
        message: "Error loading proxy data: " + err.message
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { site_uuid } = getSite();
    if (site_uuid) {
      loadProxyData(site_uuid);
      loadUnassignedProxies();
    }
  }, []);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    const field = id.replace('input-', '');

    if (type === 'checkbox') {
      setProxyData(prev => ({
        ...prev,
        [field]: field === "enable" ? (checked ? "y" : "n") : checked,
      }));
      return;
    }

    setProxyData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const waitForProxyVersionRefresh = async (site_uuid, expectedVersion) => {
    for (let i = 0; i < 10; i++) {
      const data = await getProxy(site_uuid);

      if (!expectedVersion || data.proxy_version === expectedVersion || data.update_available === false) {
        const normalized = normalizeProxyResponse(data, site_uuid);

        setProxyData(normalized);
        setSavedProxy(normalized);
        return true;
      }

      await new Promise(resolve => setTimeout(resolve, 3000));
    }

    return false;
  };

  const handleUpdateProxy = async () => {
    if (proxyUpdating) return;

    setProxyUpdating(true);
    setSaveStatus({
      show: true,
      success: true,
      message: "Updating proxy. Please wait...",
    });

    try {
      const { site_uuid } = getSite();
      if (!site_uuid) throw new Error("No site selected");

      const res = await fetch(
        `${serverHost}/hrms/proxy/update/${site_uuid}`,
        { method: "POST" }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || data.error || "Proxy update failed");
      }

      setSaveStatus({
        show: true,
        success: true,
        message: data.message || "Proxy updated successfully.",
      });

      // refresh proxy so version + update_available update immediately
      const refreshed = await waitForProxyVersionRefresh(
        site_uuid,
        data.target_version || proxyData.latest_version
      );

      if (!refreshed) {
        setProxyData(prev => ({
          ...prev,
          proxy_version: data.target_version || prev.latest_version || prev.proxy_version,
          update_available: false,
        }));

        setSavedProxy(prev => prev
          ? {
            ...prev,
            proxy_version: data.target_version || prev.proxy_version,
            update_available: false,
          }
          : prev
        );
      }

    } catch (err) {
      setSaveStatus({
        show: true,
        success: false,
        message: err.message || "Proxy update failed",
      });
    } finally {
      setProxyUpdating(false);
    }
  };

  const handleViewSupportPassword = async () => {
    if (supportPasswordLoading) return;

    const { site_uuid } = getSite();
    if (!site_uuid) {
      setSaveStatus({
        show: true,
        success: false,
        message: "No site selected.",
      });
      return;
    }

    setSupportPasswordLoading(true);
    setSupportAccess(null);

    try {
      const response = await fetch(
        `${serverHost}/hrms/proxy/support-password/${site_uuid}`,
        { method: "POST" }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(
          data.error || data.message || "The proxy could not generate a support password."
        );
      }

      setSupportAccess({
        username: data.username || "husaria",
        password: data.password || "",
        expires_at: data.expires_at || null,
      });
    } catch (error) {
      setSaveStatus({
        show: true,
        success: false,
        message: error.message || "Failed to generate support password.",
      });
    } finally {
      setSupportPasswordLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      if (!proxyData.proxy_name || !proxyData.mqtt_topic) {
        setSaveStatus({
          show: true,
          success: false,
          message: "Please fill in all required fields",
        });
        return;
      }

      const { site_uuid } = getSite();
      if (!site_uuid) {
        setSaveStatus({
          show: true,
          success: false,
          message: "No site selected",
        });
        return;
      }

      if (proxyData.salto_sync_enabled) {
        const hasPassword =
          proxyData.salto_ship_password_set ||
          Boolean(proxyData.salto_ship_password?.trim());

        if (
          !proxyData.salto_ship_url?.trim() ||
          !proxyData.salto_ship_username?.trim() ||
          !hasPassword
        ) {
          setSaveStatus({
            show: true,
            success: false,
            message: "Please enter the Salto SHIP URL, username, and password.",
          });
          return;
        }

        if (!/^https?:\/\//i.test(proxyData.salto_ship_url.trim())) {
          setSaveStatus({
            show: true,
            success: false,
            message: "Salto SHIP URL must start with http:// or https://.",
          });
          return;
        }
      }

      const networkSettings = resolveNetworkSettings(proxyData);

      const saltoSettings = {
        salto_sync_enabled: proxyData.salto_sync_enabled,
        salto_ship_url: proxyData.salto_ship_url?.trim() || "",
        salto_ship_username: proxyData.salto_ship_username?.trim() || "",
        salto_vnumber_gpf: proxyData.salto_vnumber_gpf || "GPF1",
        salto_phone1_gpf: proxyData.salto_phone1_gpf || "GPF2",
        salto_phone2_gpf: proxyData.salto_phone2_gpf || "HUSARIAONE",
        salto_phone3_gpf: proxyData.salto_phone3_gpf || "HUSARIAONE",
        salto_pin_gpf: proxyData.salto_pin_gpf || "HUSARIAONE",
      };

      // A blank password means "keep the password already stored".
      if (proxyData.salto_ship_password) {
        saltoSettings.salto_ship_password = proxyData.salto_ship_password;
      }

      const proxyRuntimeSettings = {
        proxy_checkin_interval_seconds: Number(
          proxyData.proxy_checkin_interval_seconds || 60
        ),
        device_discovery_subnets: proxyData.device_discovery_subnets?.trim() || "",
        device_discovery_probe_ports: proxyData.device_discovery_probe_ports,
        device_discovery_max_hosts: Number(
          proxyData.device_discovery_max_hosts || 512
        ),
      };

      if (!proxyData.mqtt_broker?.trim()) {
        setSaveStatus({
          show: true,
          success: false,
          message: "MQTT broker is required.",
        });
        return;
      }

      const mqttPort = Number(proxyData.mqtt_port);

      if (!Number.isInteger(mqttPort) || mqttPort < 1 || mqttPort > 65535) {
        setSaveStatus({
          show: true,
          success: false,
          message: "MQTT port must be between 1 and 65535.",
        });
        return;
      }

      const proxyConnectionSettings = {
        mqtt_broker: proxyData.mqtt_broker.trim(),
        mqtt_port: mqttPort,
        mqtt_username: proxyData.mqtt_username?.trim() || undefined,
        mqtt_tls_enabled: Boolean(proxyData.mqtt_tls_enabled),
        device_api_username:
          proxyData.device_api_username?.trim() || undefined,
      };

      if (proxyData.mqtt_password?.trim()) {
        proxyConnectionSettings.mqtt_password =
          proxyData.mqtt_password;
      }

      if (proxyData.device_api_password?.trim()) {
        proxyConnectionSettings.device_api_password =
          proxyData.device_api_password;
      }

      let proxy;
      const shouldApplyOnsiteProxyConfig = true;

      if (savedProxy?.proxy_uuid) {
        proxy = await updateProxy(savedProxy.proxy_uuid, {
          proxy_name: proxyData.proxy_name,
          proxy_version: proxyData.proxy_version,
          mqtt_topic: proxyData.mqtt_topic,
          enable: proxyData.enable,
          ...saltoSettings,
          ...proxyRuntimeSettings,
          ...proxyConnectionSettings,
          ...networkSettings,
        });
      } else {
        proxy = await registerProxy(site_uuid, {
          proxy_name: proxyData.proxy_name,
          proxy_version: proxyData.proxy_version,
          mqtt_topic: proxyData.mqtt_topic,
          enable: proxyData.enable,
          ...saltoSettings,
          ...proxyRuntimeSettings,
          ...proxyConnectionSettings,
          ...networkSettings,
        });
      }

      let onsiteConfigError = null;
      let networkRebootRequired = Boolean(proxy.requires_reboot || proxy.network_change_pending);

      // The database save above is the source of truth. This second request
      // copies those saved values into the onsite proxy's local config.py.
      if (shouldApplyOnsiteProxyConfig) {
        try {
          const configResponse = await fetch(
            `${serverHost}/hrms/proxy/config/${site_uuid}`,
            { method: "POST" }
          );

          const configResult = await configResponse.json().catch(() => ({}));

          if (!configResponse.ok || configResult.success === false) {
            throw new Error(
              configResult.error ||
              configResult.message ||
              "The onsite proxy did not accept the configuration."
            );
          }

          networkRebootRequired = networkRebootRequired || Boolean(
            configResult.requires_reboot || configResult.network_change_pending
          );

          if (configResult.runtime_reloaded === false && !networkRebootRequired) {
            throw new Error(
              configResult.runtime_reload_errors?.join("; ") ||
              "The onsite configuration was saved, but a running proxy service could not be reloaded."
            );
          }
        } catch (configError) {
          onsiteConfigError = configError.message;
        }
      }

      setSaveStatus({
        show: true,
        success: !onsiteConfigError,
        message: onsiteConfigError
          ? (
            "The settings were saved in HusariaOne, but the onsite proxy could not " +
            `fully apply them: ${onsiteConfigError} ` +
            "Verify the proxy is reachable and click Save again."
          )
          : networkRebootRequired
            ? "Network settings saved successfully. Reboot the proxy to apply them."
            : "Proxy configuration saved successfully!",
      });

      const normalized = normalizeProxyResponse(proxy, site_uuid);

      setSavedProxy(normalized);
      setProxyData(normalized);
    } catch (err) {
      console.error("Error saving proxy:", err);
      setSaveStatus({
        show: true,
        success: false,
        message: "Error saving proxy: " + err.message,
      });
    }
  };

  if (loading) {
    return (
      <>
        <HusariaHeader name="Proxy Configuration" parentName="HRMS" padding="4.5rem" />
        <Container className="mt--6" fluid>
          <Row>
            <Col xl="12">
              <Card>
                <CardBody className="py-5 text-center">
                  <Spinner />
                  <div className="mt-3 text-muted">Loading proxy configuration...</div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <HusariaHeader name="Proxy Configuration" parentName="HRMS" padding="4.5rem" />
      <Container className="mt--6" fluid>
        {saveStatus.show && (
          <Alert
            color={saveStatus.success ? "success" : "danger"}
            isOpen={saveStatus.show}
            toggle={() => setSaveStatus({ ...saveStatus, show: false })}
          >
            {saveStatus.message}
          </Alert>
        )}
        {proxyUpdating && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Card style={{ width: "420px", textAlign: "center" }}>
              <CardBody className="py-5">
                <Spinner color="info" />
                <h3 className="mt-4 mb-2">Updating Proxy</h3>
                <p className="text-muted mb-0">
                  The proxy is downloading and applying the update. Please wait.
                </p>
              </CardBody>
            </Card>
          </div>
        )}
        <Card
          className="mb-4"
          style={{
            backgroundColor: isDark ? "#1f2937" : "#ffffff",
            borderColor: isDark ? "#374151" : "#e5e7eb",
            color: isDark ? "#f9fafb" : "#111827",
          }}
        >
          <CardBody className="pb-3">
            <div className="d-flex align-items-start justify-content-between mb-3">
              <div>
                <h6 className="heading-small mb-2" style={sectionHeadingStyle}>
                  Unassigned Proxies
                </h6>

                <small style={secondaryTextStyle}>
                  Proxies that have enrolled but have not been assigned to a site.
                </small>
              </div>

              <Button
                color="secondary"
                size="sm"
                onClick={loadUnassignedProxies}
                disabled={unassignedLoading}
              >
                {unassignedLoading ? "Refreshing..." : "Refresh"}
              </Button>
            </div>

            {unassignedLoading && unassignedProxies.length === 0 ? (
              <div className="text-center py-3" style={secondaryTextStyle}>
                <Spinner size="sm" />
              </div>
            ) : unassignedProxies.length === 0 ? (
              <div className="small py-2" style={secondaryTextStyle}>
                No unassigned proxies are currently waiting for assignment.
              </div>
            ) : (
              <>
                <div className="small mb-3" style={secondaryTextStyle}>
                  Assignment target:{" "}
                  <strong style={metadataLabelStyle}>{getSite().site_uuid}</strong>
                </div>

                {unassignedProxies.map((proxy) => {
                  const draft = assignmentDrafts[proxy.proxy_uuid] || {};
                  const isAssigning = assigningProxyUuid === proxy.proxy_uuid;

                  return (
                    <div
                      key={proxy.proxy_uuid}
                      className="border rounded p-3 mb-3"
                      style={{
                        backgroundColor: isDark ? "#111827" : "#fafafa",
                        borderColor: isDark ? "#4b5563" : "#dee2e6",
                      }}
                    >
                      <Row className="align-items-end">
                        <Col lg="3">
                          <div className="small" style={secondaryTextStyle}>
                            <div className="mb-1" style={metadataLabelStyle}>
                              Proxy UUID
                            </div>
                            <div className="text-break">{proxy.proxy_uuid}</div>
                          </div>
                        </Col>

                        <Col lg="4">
                          <FormGroup className="mb-lg-0">
                            <label
                              className="form-control-label"
                              htmlFor={`enrollment-code-${proxy.proxy_uuid}`}
                              style={labelStyle}
                            >
                              Enrollment code
                            </label>

                            <div
                              className="form-control d-flex align-items-center"
                              style={{ padding: 0, overflow: "hidden" }}
                            >
                              <Input
                                id={`enrollment-code-${proxy.proxy_uuid}`}
                                type="text"
                                value={draft.enrollment_code || ""}
                                onChange={(event) =>
                                  handleAssignmentDraftChange(
                                    proxy.proxy_uuid,
                                    "enrollment_code",
                                    event.target.value
                                  )
                                }
                                placeholder="First 4 characters"
                                minLength={4}
                                maxLength={4}
                                required
                                autoComplete="off"
                                style={{
                                  border: 0,
                                  boxShadow: "none",
                                  backgroundColor: "transparent",
                                  flex: "1 1 auto",
                                  minWidth: 0,
                                }}
                              />
                              <span
                                className="pr-3"
                                style={{ ...secondaryTextStyle, flexShrink: 0 }}
                                aria-label="Last four enrollment-code characters"
                              >
                                -{proxy.enrollment_code_hint || "----"}
                              </span>
                            </div>
                          </FormGroup>
                        </Col>

                        <Col lg="3">
                          <FormGroup className="mb-lg-0">
                            <label
                              className="form-control-label"
                              style={labelStyle}
                            >
                              MQTT Topic
                            </label>

                            <Input
                              value={draft.mqtt_topic || ""}
                              onChange={(event) =>
                                handleAssignmentDraftChange(
                                  proxy.proxy_uuid,
                                  "mqtt_topic",
                                  event.target.value
                                )
                              }
                              placeholder="Enter MQTT topic"
                            />
                          </FormGroup>
                        </Col>

                        <Col lg="2" className="text-lg-right">
                          <div className="small mb-2" style={secondaryTextStyle}>
                            Last check-in:{" "}
                            {proxy.last_checkin
                              ? new Date(proxy.last_checkin).toLocaleString()
                              : "Never"}
                          </div>

                          <Button
                            color="primary"
                            onClick={() => handleAssignProxy(proxy)}
                            disabled={isAssigning}
                          >
                            {isAssigning ? "Assigning..." : "Assign"}
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  );
                })}
              </>
            )}
          </CardBody>
        </Card>
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card>
              <CardHeader
                className="border-0 header-theme" style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}>
                <Row className="align-items-center">
                  <Col xs="8">
                  </Col>
                  <Col xs="4" className="d-flex justify-content-end">
                    {savedProxy?.proxy_uuid && (
                      <Button
                        color="secondary"
                        size="sm"
                        className="mr-2"
                        onClick={handleViewSupportPassword}
                        disabled={supportPasswordLoading || proxyUpdating}
                      >
                        {supportPasswordLoading ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Generating...
                          </>
                        ) : (
                          "Generate Support Password"
                        )}
                      </Button>
                    )}
                    {proxyData.update_available && (
                      <Button
                        color="success"
                        size="sm"
                        className="mr-2"
                        onClick={handleUpdateProxy}
                        disabled={proxyUpdating}
                      >
                        {proxyUpdating ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            Updating...
                          </>
                        ) : (
                          "Update Proxy"
                        )}
                      </Button>
                    )}
                    <Button
                      color="info"
                      onClick={handleSave}
                      size="sm"
                    >
                      Save
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              {supportAccess && (
                <CardBody
                  className="border-bottom"
                  style={{
                    backgroundColor: isDark ? "#111827" : "#f8fafc",
                  }}
                >
                  <div className="d-flex align-items-start justify-content-between">
                    <div>
                      <h6 className="mb-2" style={sectionHeadingStyle}>
                        Temporary Support Access
                      </h6>
                      <div className="small" style={secondaryTextStyle}>
                        Use these credentials to connect as the <strong>husaria</strong>
                        {supportAccess.expires_at
                          ? ` account until ${new Date(supportAccess.expires_at).toLocaleString()}.`
                          : "."}
                        Existing sessions remain active after expiration.
                      </div>
                    </div>
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => setSupportAccess(null)}
                      style={secondaryTextStyle}
                    >
                      Hide
                    </Button>
                  </div>

                  <Row className="mt-3">
                    <Col md="4">
                      <FormGroup className="mb-md-0">
                        <label className="form-control-label" style={labelStyle}>
                          Username
                        </label>
                        <Input value={supportAccess.username} readOnly />
                      </FormGroup>
                    </Col>
                    <Col md="8">
                      <FormGroup className="mb-0">
                        <label className="form-control-label" style={labelStyle}>
                          Temporary password
                        </label>
                        <Input value={supportAccess.password} readOnly />
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              )}
              <CardBody>
                <Form>
                  <h6 className="heading-small mb-4" style={sectionHeadingStyle}>
                    Proxy information
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-proxy_name"
                            style={labelStyle}
                          >
                            Proxy Name <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-proxy_name"
                            value={proxyData.proxy_name}
                            onChange={handleInputChange}
                            placeholder="Enter proxy name"
                            type="text"
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-mqtt_topic"
                            style={labelStyle}
                          >
                            MQTT Topic <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-mqtt_topic"
                            value={proxyData.mqtt_topic}
                            onChange={handleInputChange}
                            placeholder="Enter MQTT topic"
                            type="text"
                          //disabled
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-proxy_version"
                            style={labelStyle}
                          >
                            Version
                          </label>
                          <div className="d-flex align-items-center">
                            <Input
                              id="input-proxy_version"
                              value={proxyData.proxy_version || "Unknown"}
                              readOnly
                            />

                            {proxyData.update_available && (
                              <Badge
                                color="warning"
                                className="ml-2"
                                style={{
                                  height: "28px",
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "0 10px",
                                  fontWeight: 600,
                                }}
                              >
                                Update Available
                              </Badge>
                            )}
                          </div>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-proxy_ip"
                            style={labelStyle}
                          >
                            Current IP Address
                          </label>
                          <Input
                            id="input-proxy_ip"
                            value={proxyData.proxy_ip}
                            placeholder="Reported by proxy check-ins"
                            type="text"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-proxy_uuid"
                            style={labelStyle}
                          >
                            Proxy UUID
                          </label>
                          <Input
                            id="input-proxy_uuid"
                            value={proxyData.proxy_uuid}
                            onChange={handleInputChange}
                            placeholder="Enter proxy UUID"
                            type="text"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-site_id"
                            style={labelStyle}
                          >
                            Site UUID
                          </label>
                          <Input
                            id="input-site_id"
                            value={proxyData.site_id}
                            onChange={handleInputChange}
                            placeholder="Enter site UUID"
                            type="text"
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-enable"
                            style={labelStyle}
                          >
                            Enable
                          </label>
                          <div>
                            <label className="custom-toggle">
                              <input
                                type="checkbox"
                                id="input-enable"
                                checked={proxyData.enable === 'y'}
                                onChange={handleInputChange}
                              />
                              <span
                                className="custom-toggle-slider rounded-circle"
                                data-label-off="No"
                                data-label-on="Yes"
                              />
                            </label>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-status"
                            style={labelStyle}
                          >
                            Status
                          </label>
                          <div className="d-flex align-items-center">
                            <div style={{ marginRight: "10px" }}>
                              {savedProxy?.last_checkin ? (
                                <Badge
                                  color={
                                    Date.now() - new Date(savedProxy.last_checkin).getTime() <= 5 * 60 * 1000
                                      ? "success"
                                      : "danger"
                                  }
                                >
                                  {Date.now() - new Date(savedProxy.last_checkin).getTime() <= 5 * 60 * 1000
                                    ? "Online"
                                    : "Offline"}
                                </Badge>
                              ) : (
                                <Badge color="warning" style={{ color: "#ff3709" }}>
                                  Never Connected
                                </Badge>
                              )}
                            </div>

                            {savedProxy?.last_checkin && (
                              <div className="form-control-label" style={labelStyle}>
                                Last check-in: {new Date(savedProxy.last_checkin).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                  <hr className="my-4" />

                  <h6 className="heading-small mb-4" style={sectionHeadingStyle}>
                    Salto SHIP Sync
                  </h6>

                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-salto_sync_enabled"
                            style={labelStyle}
                          >
                            Enable Salto SHIP Sync
                          </label>

                          <div>
                            <label className="custom-toggle">
                              <input
                                type="checkbox"
                                id="input-salto_sync_enabled"
                                checked={Boolean(proxyData.salto_sync_enabled)}
                                onChange={handleInputChange}
                              />
                              <span
                                className="custom-toggle-slider rounded-circle"
                                data-label-off="No"
                                data-label-on="Yes"
                              />
                            </label>
                          </div>

                          <small className="form-text text-muted">
                            Enable tenant syncing through the SHIP server configured in Salto Space.
                          </small>
                        </FormGroup>
                      </Col>

                      <Col lg="6" className="d-flex align-items-start justify-content-lg-end">
                        <Button
                          color="secondary"
                          size="sm"
                          type="button"
                          onClick={() => setSaltoShipOpen(prev => !prev)}
                          style={{
                            marginTop: "1.95rem",
                            backgroundColor: isDark ? "#374151" : undefined,
                            borderColor: isDark ? "#4b5563" : undefined,
                            color: isDark ? "#ffffff" : undefined,
                          }}
                        >
                          {saltoShipOpen ? "Hide GPF Fields" : "Configure GPF Fields"}
                        </Button>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-salto_ship_url"
                            style={labelStyle}
                          >
                            Salto SHIP Server URL
                          </label>

                          <Input
                            id="input-salto_ship_url"
                            type="url"
                            value={proxyData.salto_ship_url || ""}
                            onChange={handleInputChange}
                            disabled={!proxyData.salto_sync_enabled}
                            placeholder="Enter Salto Space URL"
                            required={proxyData.salto_sync_enabled}
                          />

                          <small className="form-text text-muted">
                            HTTP(S) endpoint for this site's Salto SHIP service.
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-salto_ship_username"
                            style={labelStyle}
                          >
                            Salto SHIP Username
                          </label>

                          <Input
                            id="input-salto_ship_username"
                            type="text"
                            value={proxyData.salto_ship_username || ""}
                            onChange={handleInputChange}
                            disabled={!proxyData.salto_sync_enabled}
                            autoComplete="off"
                            required={proxyData.salto_sync_enabled}
                            placeholder="Enter Salto SHIP username"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-salto_ship_password"
                            style={labelStyle}
                          >
                            Salto SHIP Password
                          </label>
                          <div style={{ position: "relative" }}>
                            <Input
                              id="input-salto_ship_password"
                              type={showSaltoShipPassword ? "text" : "password"}
                              value={proxyData.salto_ship_password || ""}
                              onChange={handleInputChange}
                              disabled={!proxyData.salto_sync_enabled}
                              autoComplete="new-password"
                              placeholder={
                                proxyData.salto_ship_password_set
                                  ? "Password already saved"
                                  : "Enter the SHIP Basic Auth password"
                              }
                              required={
                                proxyData.salto_sync_enabled &&
                                !proxyData.salto_ship_password_set
                              }
                              style={{ paddingRight: "2.75rem" }}
                            />

                            <button
                              type="button"
                              onClick={() =>
                                setShowSaltoShipPassword((currentlyShown) => !currentlyShown)
                              }
                              disabled={
                                !proxyData.salto_sync_enabled ||
                                !proxyData.salto_ship_password
                              }
                              aria-label={
                                showSaltoShipPassword ? "Hide password" : "Show password"
                              }
                              title={
                                showSaltoShipPassword ? "Hide password" : "Show password"
                              }
                              style={{
                                position: "absolute",
                                top: "50%",
                                right: "0.85rem",
                                transform: "translateY(-50%)",
                                padding: 0,
                                border: "none",
                                background: "transparent",
                                color: "#8898aa",
                                cursor:
                                  proxyData.salto_sync_enabled &&
                                    proxyData.salto_ship_password
                                    ? "pointer"
                                    : "default",
                                opacity:
                                  proxyData.salto_sync_enabled &&
                                    proxyData.salto_ship_password
                                    ? 1
                                    : 0.4,
                                lineHeight: 1,
                                zIndex: 2,
                              }}
                            >
                              <i
                                className={
                                  showSaltoShipPassword ? "fas fa-eye-slash" : "fas fa-eye"
                                }
                                aria-hidden="true"
                              />
                            </button>
                          </div>

                          <small className="form-text text-muted">
                            {proxyData.salto_ship_password_set
                              ? "Leave blank to keep the saved password. Enter a value to replace it."
                              : "Enter the password used by the Salto SHIP service."}
                          </small>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Collapse isOpen={saltoShipOpen} style={{ width: "100%" }}>
                      <div style={{ width: "100%" }}>
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-salto_vnumber_gpf"
                                style={labelStyle}
                              >
                                Virtual Number Field
                              </label>

                              <Input
                                id="input-salto_vnumber_gpf"
                                type="select"
                                value={proxyData.salto_vnumber_gpf || "GPF1"}
                                onChange={handleInputChange}
                                disabled={!proxyData.salto_sync_enabled}
                              >
                                {gpfOptions.map(option => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Input>

                              <small className="form-text text-muted">
                                Salto GPF field used for tenant virtual number.
                              </small>
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-salto_phone1_gpf"
                                style={labelStyle}
                              >
                                Phone Number Field
                              </label>

                              <Input
                                id="input-salto_phone1_gpf"
                                type="select"
                                value={proxyData.salto_phone1_gpf || "GPF2"}
                                onChange={handleInputChange}
                                disabled={!proxyData.salto_sync_enabled}
                              >
                                {gpfOptions.map(option => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </Input>

                              <small className="form-text text-muted">
                                Salto GPF field used for Phone 1.
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          {[
                            {
                              key: "salto_phone2_gpf",
                              label: "Phone Number 2",
                              help: "Choose a Salto GPF field, or HusariaOne to keep Phone 2 editable.",
                            },
                            {
                              key: "salto_phone3_gpf",
                              label: "Phone Number 3",
                              help: "Choose a Salto GPF field, or HusariaOne to keep Phone 3 editable.",
                            },
                            {
                              key: "salto_pin_gpf",
                              label: "PIN",
                              help: "Choose a Salto GPF field, or HusariaOne to keep the PIN editable.",
                            },
                          ].map((field) => (
                            <Col lg="4" key={field.key}>
                              <FormGroup>
                                <label
                                  className="form-control-label"
                                  htmlFor={`input-${field.key}`}
                                  style={labelStyle}
                                >
                                  {field.label}
                                </label>

                                <Input
                                  id={`input-${field.key}`}
                                  type="select"
                                  value={proxyData[field.key]}
                                  onChange={handleInputChange}
                                  disabled={!proxyData.salto_sync_enabled}
                                >
                                  {optionalSaltoFieldOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Input>

                                <small className="form-text text-muted">
                                  {field.help}
                                </small>
                              </FormGroup>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </Collapse>
                  </div>

                  <hr className="my-4" />

                  <h6 className="heading-small mb-4" style={sectionHeadingStyle}>
                    Network Configuration
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label" htmlFor="input-network_mode" style={labelStyle}>
                            Network Mode
                          </label>
                          <Input id="input-network_mode" type="select" value={proxyData.network_mode} onChange={handleInputChange}>
                            <option value="dhcp">DHCP</option>
                            <option value="static">Static IP</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    {proxyData.network_mode === "static" && (
                      <Row>
                        {[
                          { key: "static_ip", label: "Static IP Address", placeholder: "192.168.1.50" },
                          { key: "static_prefix", label: "Subnet Prefix", placeholder: "24", type: "number" },
                          { key: "static_gateway", label: "Gateway", placeholder: "192.168.1.1" },
                          { key: "static_dns", label: "DNS Servers", placeholder: "192.168.1.1,1.1.1.1" },
                        ].map((field) => (
                          <Col lg="6" key={field.key}>
                            <FormGroup>
                              <label className="form-control-label" htmlFor={`input-${field.key}`} style={labelStyle}>
                                {field.label}
                              </label>
                              <Input
                                id={`input-${field.key}`}
                                type={field.type || "text"}
                                value={proxyData[field.key]}
                                onChange={handleInputChange}
                                placeholder={field.placeholder}
                                required
                                min={field.key === "static_prefix" ? 1 : undefined}
                                max={field.key === "static_prefix" ? 32 : undefined}
                                step={field.key === "static_prefix" ? 1 : undefined}
                              />
                            </FormGroup>
                          </Col>
                        ))}
                      </Row>
                    )}
                    <small className="form-text text-muted">
                      Network changes take effect after the proxy is rebooted. The current IP is reported by check-ins.
                    </small>
                  </div>
                  <hr className="my-4" />

                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <h6 className="heading-small mb-0" style={sectionHeadingStyle}>
                      Advanced Proxy Settings
                    </h6>
                    <Button
                      color="secondary"
                      size="sm"
                      type="button"
                      onClick={() => setAdvancedProxyOpen(prev => !prev)}
                      style={{
                        backgroundColor: isDark ? "#374151" : undefined,
                        borderColor: isDark ? "#4b5563" : undefined,
                        color: isDark ? "#ffffff" : undefined,
                      }}
                    >
                      {advancedProxyOpen
                        ? "Hide Advanced Settings"
                        : "Show Advanced Settings"}
                    </Button>
                  </div>

                  <Collapse isOpen={advancedProxyOpen} style={{ width: "100%" }}>
                    <div className="pl-lg-4" style={{ width: "100%" }}>
                      <Row>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-mqtt_broker"
                              style={labelStyle}
                            >
                              MQTT Broker
                            </label>

                            <Input
                              id="input-mqtt_broker"
                              type="text"
                              value={proxyData.mqtt_broker}
                              onChange={handleInputChange}
                            />
                          </FormGroup>
                        </Col>

                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-mqtt_port"
                              style={labelStyle}
                            >
                              MQTT Port
                            </label>

                            <Input
                              id="input-mqtt_port"
                              type="number"
                              min="1"
                              max="65535"
                              value={proxyData.mqtt_port}
                              onChange={handleInputChange}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-mqtt_username"
                              style={labelStyle}
                            >
                              MQTT Username
                            </label>

                            <Input
                              id="input-mqtt_username"
                              type="text"
                              value={proxyData.mqtt_username}
                              onChange={handleInputChange}
                            />
                          </FormGroup>
                        </Col>

                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-mqtt_password"
                              style={labelStyle}
                            >
                              MQTT Password
                            </label>

                            <Input
                              id="input-mqtt_password"
                              type="password"
                              placeholder={
                                proxyData.mqtt_password_set
                                  ? "Password already saved"
                                  : "Enter MQTT password"
                              }
                              value={proxyData.mqtt_password}
                              onChange={handleInputChange}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-device_api_username"
                              style={labelStyle}
                            >
                              Device API Username
                            </label>

                            <Input
                              id="input-device_api_username"
                              type="text"
                              value={proxyData.device_api_username}
                              onChange={handleInputChange}
                            />
                          </FormGroup>
                        </Col>

                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-device_api_password"
                              style={labelStyle}
                            >
                              Device API Password
                            </label>

                            <Input
                              id="input-device_api_password"
                              type="password"
                              placeholder={
                                proxyData.device_api_password_set
                                  ? "Password already saved"
                                  : "Enter device API password"
                              }
                              value={proxyData.device_api_password}
                              onChange={handleInputChange}
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-proxy_checkin_interval_seconds"
                              style={labelStyle}
                            >
                              Check-in Interval (seconds)
                            </label>
                            <Input
                              id="input-proxy_checkin_interval_seconds"
                              type="number"
                              min="5"
                              value={proxyData.proxy_checkin_interval_seconds}
                              onChange={handleInputChange}
                            />
                            <small className="form-text text-muted">
                              How often the onsite proxy reports its status to HusariaOne.
                            </small>
                          </FormGroup>
                        </Col>

                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-device_discovery_max_hosts"
                              style={labelStyle}
                            >
                              Maximum Discovery Hosts
                            </label>
                            <Input
                              id="input-device_discovery_max_hosts"
                              type="number"
                              min="1"
                              value={proxyData.device_discovery_max_hosts}
                              onChange={handleInputChange}
                            />
                            <small className="form-text text-muted">
                              Maximum number of hosts checked during device discovery.
                            </small>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-device_discovery_subnets"
                              style={labelStyle}
                            >
                              Discovery Subnets
                            </label>
                            <Input
                              id="input-device_discovery_subnets"
                              type="text"
                              value={proxyData.device_discovery_subnets}
                              onChange={handleInputChange}
                              placeholder="Leave blank for automatic discovery"
                            />
                            <small className="form-text text-muted">
                              Optional comma-separated CIDR ranges, such as 10.1.110.0/24.
                            </small>
                          </FormGroup>
                        </Col>

                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-device_discovery_probe_ports"
                              style={labelStyle}
                            >
                              Discovery Probe Ports
                            </label>
                            <Input
                              id="input-device_discovery_probe_ports"
                              type="text"
                              value={Array.isArray(proxyData.device_discovery_probe_ports)
                                ? proxyData.device_discovery_probe_ports.join(", ")
                                : proxyData.device_discovery_probe_ports || ""}
                              onChange={handleInputChange}
                              placeholder="80, 443, 5060"
                            />
                            <small className="form-text text-muted">
                              Comma-separated TCP ports used during discovery.
                            </small>
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row className="mt-3">
                        <Col lg="12">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "8px",
                            }}
                          >
                            <Input
                              id="input-mqtt_tls_enabled"
                              type="checkbox"
                              checked={Boolean(proxyData.mqtt_tls_enabled)}
                              onChange={handleInputChange}
                              style={{
                                width: "auto",
                                marginTop: "4px",
                                flex: "0 0 auto",
                              }}
                            />

                            <div>
                              <label
                                className="form-control-label"
                                htmlFor="input-mqtt_tls_enabled"
                                style={{
                                  ...labelStyle,
                                  display: "block",
                                  marginBottom: "4px",
                                  cursor: "pointer",
                                }}
                              >
                                Enable MQTT TLS
                              </label>

                              <small className="form-text text-muted">
                                The CA certificate is managed on the proxy and is not edited here.
                              </small>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Collapse>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ProxyForm;
